import { useEffect, useState } from 'react'

import {
    CompanyAccountActivityStatusFilter,
    CompanyFilter,
    DatePicker,
    Feature,
    FormControl,
    SearchableSelect,
    Select
} from '@/components'
import { BatteryEligibilityTypeEnum, CompanyTypeEnum, UserRoleEnum } from '@/enums'
import { Company, CompanyBatteryEligibility, CompanyMonitoringStatus, CompanyType } from '@/models'
import { AccountManagerDataType, CompanyIndexFiltersType, FormChangeEvent, FormChangeEventHandler } from '@/types'

type CompanyIndexFiltersProps = {
    standalone?: boolean
    filters: CompanyIndexFiltersType
    onChange: FormChangeEventHandler
}

export const CompanyIndexFilters = ({
    standalone,
    filters,
    ...props
}: CompanyIndexFiltersProps) => {
    const [accountManagers, setAccountManagers] = useState<AccountManagerDataType[]>([])

    const fetchAccountManagers = async () => {
        const res = await Company.accountManagers()
        setAccountManagers(res.data)
    }

    useEffect(() => {
        fetchAccountManagers()
    }, [])

    useEffect(() => {
        if (filters.battery_eligibility.value) {
            const companyBatteryEligibility = CompanyBatteryEligibility
                .find(filters.battery_eligibility.value as BatteryEligibilityTypeEnum)
            props.onChange({
                target: {
                    name: 'battery_eligibility',
                    value: filters.battery_eligibility.value,
                    displayValue: companyBatteryEligibility ? companyBatteryEligibility.title : ''
                }
            })
        }
        if (filters.type.value) {
            const companyType = CompanyType.find(filters.type.value as CompanyTypeEnum)
            props.onChange({
                target: {
                    name: 'type',
                    value: filters.type.value,
                    displayValue: companyType ? companyType.title : ''
                }
            })
        }
    }, [])

    const handleChange = (e: FormChangeEvent) => {
        const { name, value } = e.target
        if (name === 'battery_eligibility') {
            const companyBatteryEligibility = CompanyBatteryEligibility
                .find(value as BatteryEligibilityTypeEnum)
            const displayValue = companyBatteryEligibility ? companyBatteryEligibility.title : ''
            props.onChange({ target: { name, value, displayValue } })
        } else if (name === 'type') {
            const companyType = CompanyType.find(value as CompanyTypeEnum)
            const displayValue = companyType ? companyType.title : ''
            props.onChange({ target: { name, value, displayValue } })
        } else {
            props.onChange(e)
        }
    }

    if (standalone) {
        return <Select
            name="monitoring_status"
            id="monitoring-status"
            options={[
                ...CompanyMonitoringStatus.all.filter(item => !item.isUnknown).map(item => ({
                    value: item.key,
                    title: <div className="flex gap-2 items-center">
                        {item.getBadge('xs')} {item.raw.title}
                    </div>
                })),
                { value: '', title: 'Any Monitoring Status' }
            ]}
            value={filters.monitoring_status.value}
            onChange={handleChange}
            className="md:min-w-75"
        />
    }
    return <>
        <Select
            id="company-type"
            name="type"
            label={filters.type.label}
            options={[
                { value: '', title: 'Any Company Type' },
                ...CompanyType.all.map(item => ({ value: item.key, title: item.title }))
            ]}
            value={filters.type.value}
            onChange={handleChange}
            input-size="xs"
        />

        <CompanyFilter
            id="affiliate-id"
            name="conglomerate_id"
            label="Affiliate"
            type={UserRoleEnum.AFFILIATE}
            placeholder="Any Affiliate"
            value={filters.conglomerate_id.value}
            onChange={handleChange}
            data-test="companies-index-affiliate-id-filter"
            input-size="xs"
        />

        <Feature name="new-company-page-filters">
            <FormControl label={filters.company_account_activity_statuses.label}>
                <CompanyAccountActivityStatusFilter
                    id="company-account-activity-status"
                    name="company_account_activity_statuses"
                    value={filters.company_account_activity_statuses.value}
                    onChange={handleChange}
                    data-test="company-account-activity-status-filter"
                />
            </FormControl>
        </Feature>

        <Select
            id="battery-eligibility-filter"
            name="battery_eligibility"
            label={filters.battery_eligibility.label}
            options={[
                { value: '', title: 'Any Battery Eligibility' },
                ...CompanyBatteryEligibility.all.map(item => ({
                    value: item.key,
                    title: <div className="flex gap-2 items-center">
                        {item.icon()} {item.title}
                    </div>
                }))
            ]}
            value={filters.battery_eligibility.value}
            onChange={handleChange}
            input-size="xs"
        />

        <Feature name="new-company-page-filters">
            <SearchableSelect
                id="account-manager-email"
                name="account_manager_email"
                label={filters.account_manager_email.label}
                placeholder="Any Account Manager"
                options={accountManagers.map(item => ({
                    value: item.email,
                    title: item.name
                }))}
                value={filters.account_manager_email.value}
                onChange={handleChange}
                data-test="companies-index-account-manager-email-filter"
                input-size="xs"
            />
        </Feature>

        <DatePicker
            id="last_warranty_created_date_range"
            name="last_warranty_created_date_range"
            label={filters.last_warranty_created_date_range.label}
            clearable
            placeholder="Select Dates"
            value={filters.last_warranty_created_date_range.value}
            onChange={handleChange}
            singleMode={false}
            maxDate={new Date()}
            data-test="companies-index-last-warranty-created-date-range-filter"
            input-size="xs"
        />
    </>
}
